import React, { useEffect, useState, useCallback } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Users, Plus, KeyRound, Pencil, Shield, Building2, RefreshCw, Copy, Search } from "lucide-react";

const genPassword = () => {
    const chars = "abcdefghjkmnpqrstuvwxyzABCDEFGHJKLMNPQRSTUVWXYZ23456789";
    return Array.from({ length: 10 }, () => chars[Math.floor(Math.random() * chars.length)]).join("");
};

const emptyForm = { full_name: "", email: "", password: "", branch_scope: "all", branch_ids: [] };

export default function PortalUsers() {
    const { user } = useAuth();
    const [users, setUsers] = useState([]);
    const [branches, setBranches] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editing, setEditing] = useState(null);        // user being edited, or null for create
    const [form, setForm] = useState(emptyForm);
    const [pwUser, setPwUser] = useState(null);          // user for password reset
    const [newPw, setNewPw] = useState("");
    const [saving, setSaving] = useState(false);
    const [branchQuery, setBranchQuery] = useState("");

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [u, b] = await Promise.all([api.get("/portal/users"), api.get("/branches")]);
            setUsers(u.data || []);
            setBranches(b.data || []);
        } catch (e) {
            toast.error("No se pudieron cargar los usuarios");
        } finally { setLoading(false); }
    }, []);

    useEffect(() => { if (user?.is_client_admin) load(); }, [load, user]);

    if (user && !user.is_client_admin) return <Navigate to="/portal" replace />;

    const branchName = (id) => branches.find((b) => b.id === id)?.name || "—";

    const openCreate = () => {
        setEditing(null);
        setBranchQuery("");
        setForm({ ...emptyForm, password: genPassword() });
        setDialogOpen(true);
    };

    const openEdit = (u) => {
        setEditing(u);
        setBranchQuery("");
        setForm({
            full_name: u.full_name || "",
            email: u.email || "",
            password: "",
            branch_scope: u.branch_scope || "all",
            branch_ids: u.branch_ids || [],
        });
        setDialogOpen(true);
    };

    const toggleBranch = (id) => {
        setForm((f) => ({
            ...f,
            branch_ids: f.branch_ids.includes(id)
                ? f.branch_ids.filter((x) => x !== id)
                : [...f.branch_ids, id],
        }));
    };

    const submit = async () => {
        if (!form.full_name.trim()) return toast.error("Ingrese el nombre");
        if (form.branch_scope === "specific" && form.branch_ids.length === 0)
            return toast.error("Seleccione al menos una sucursal");
        setSaving(true);
        try {
            if (editing) {
                await api.patch(`/portal/users/${editing.id}`, {
                    full_name: form.full_name,
                    branch_scope: form.branch_scope,
                    branch_ids: form.branch_ids,
                });
                toast.success("Usuario actualizado");
            } else {
                if (!form.email.trim()) return toast.error("Ingrese un correo");
                if (form.password.length < 6) return toast.error("Contraseña mínima de 6 caracteres");
                const res = await api.post("/portal/users", {
                    full_name: form.full_name,
                    email: form.email.trim().toLowerCase(),
                    password: form.password,
                    branch_scope: form.branch_scope,
                    branch_ids: form.branch_ids,
                });
                if (res.data?.email_sent) {
                    toast.success("Usuario creado. Se enviaron las credenciales por correo.");
                } else {
                    toast.success("Usuario creado.");
                    toast.warning("No se pudo enviar el correo de credenciales. Compártalas manualmente.");
                }
            }
            setDialogOpen(false);
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "No se pudo guardar");
        } finally { setSaving(false); }
    };

    const toggleActive = async (u) => {
        try {
            await api.patch(`/portal/users/${u.id}`, { is_active: !u.is_active });
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "No se pudo actualizar");
        }
    };

    const submitPassword = async () => {
        if (newPw.length < 6) return toast.error("Contraseña mínima de 6 caracteres");
        try {
            await api.post(`/portal/users/${pwUser.id}/password`, { password: newPw });
            toast.success("Contraseña actualizada");
            setPwUser(null); setNewPw("");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "No se pudo actualizar");
        }
    };

    return (
        <div className="space-y-6" data-testid="portal-users-page">
            <div className="flex items-start justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-bold flex items-center gap-2">
                        <Users className="w-6 h-6" /> Usuarios
                    </h1>
                    <p className="text-sm text-muted-foreground mt-1">
                        Cree accesos para su equipo y defina a qué sucursales puede entrar cada uno.
                    </p>
                </div>
                <Button onClick={openCreate} data-testid="portal-user-create-btn" className="shrink-0">
                    <Plus className="w-4 h-4 mr-1" /> Nuevo usuario
                </Button>
            </div>

            {loading ? (
                <p className="text-sm text-muted-foreground">Cargando…</p>
            ) : (
                <div className="grid gap-3">
                    {users.map((u) => (
                        <div key={u.id}
                            className="border rounded-xl p-4 flex flex-wrap items-center gap-3 bg-card"
                            data-testid={`portal-user-row-${u.id}`}>
                            <div className="flex-1 min-w-[200px]">
                                <div className="font-semibold flex items-center gap-2">
                                    {u.full_name}
                                    {u.is_client_admin && (
                                        <Badge variant="secondary" className="gap-1"><Shield className="w-3 h-3" /> Administrador</Badge>
                                    )}
                                    {!u.is_active && <Badge variant="destructive">Inactivo</Badge>}
                                </div>
                                <div className="text-xs text-muted-foreground">{u.email || u.username}</div>
                                <div className="text-xs mt-1 flex items-center gap-1.5 text-muted-foreground">
                                    <Building2 className="w-3.5 h-3.5" />
                                    {u.branch_scope === "all"
                                        ? "Todas las sucursales"
                                        : (u.branch_ids || []).map(branchName).join(", ") || "Sin sucursales"}
                                </div>
                            </div>
                            <div className="flex items-center gap-2">
                                <Button variant="outline" size="sm" onClick={() => { setPwUser(u); setNewPw(genPassword()); }} data-testid={`portal-user-pw-${u.id}`}>
                                    <KeyRound className="w-4 h-4 mr-1" /> Contraseña
                                </Button>
                                {!u.is_client_admin && (
                                    <Button variant="outline" size="sm" onClick={() => openEdit(u)} data-testid={`portal-user-edit-${u.id}`}>
                                        <Pencil className="w-4 h-4 mr-1" /> Editar
                                    </Button>
                                )}
                                {!u.is_primary && (
                                    <div className="flex items-center gap-1.5 pl-1">
                                        <Switch checked={u.is_active} onCheckedChange={() => toggleActive(u)} data-testid={`portal-user-active-${u.id}`} />
                                        <span className="text-xs text-muted-foreground">Activo</span>
                                    </div>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Create / Edit dialog */}
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogContent className="max-w-lg" data-testid="portal-user-dialog">
                    <DialogHeader>
                        <DialogTitle>{editing ? "Editar usuario" : "Nuevo usuario"}</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-1">
                        <div>
                            <Label>Nombre completo</Label>
                            <Input value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })}
                                placeholder="Ej. Josué Gavidia" data-testid="portal-user-name" />
                        </div>
                        {!editing && (
                            <>
                                <div>
                                    <Label>Correo</Label>
                                    <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                                        placeholder="correo@empresa.com" data-testid="portal-user-email" />
                                </div>
                                <div>
                                    <Label>Contraseña</Label>
                                    <div className="flex gap-2">
                                        <Input value={form.password} readOnly
                                            className="bg-muted/50 cursor-default font-mono"
                                            data-testid="portal-user-password" />
                                        <Button type="button" variant="outline" size="icon" title="Generar"
                                            onClick={() => setForm({ ...form, password: genPassword() })}>
                                            <RefreshCw className="w-4 h-4" />
                                        </Button>
                                        <Button type="button" variant="outline" size="icon" title="Copiar"
                                            onClick={() => { navigator.clipboard?.writeText(form.password); toast.success("Copiada"); }}>
                                            <Copy className="w-4 h-4" />
                                        </Button>
                                    </div>
                                    <p className="text-xs text-muted-foreground mt-1">Contraseña segura generada automáticamente. Regenérela si lo desea y compártala con la persona.</p>
                                </div>
                            </>
                        )}
                        <div>
                            <Label className="mb-2 block">Acceso a sucursales</Label>
                            <RadioGroup value={form.branch_scope}
                                onValueChange={(v) => setForm({ ...form, branch_scope: v })}
                                className="space-y-1">
                                <div className="flex items-center gap-2">
                                    <RadioGroupItem value="all" id="scope-all" data-testid="portal-user-scope-all" />
                                    <Label htmlFor="scope-all" className="font-normal cursor-pointer">Todas las sucursales</Label>
                                </div>
                                <div className="flex items-center gap-2">
                                    <RadioGroupItem value="specific" id="scope-specific" data-testid="portal-user-scope-specific" />
                                    <Label htmlFor="scope-specific" className="font-normal cursor-pointer">Sucursales específicas</Label>
                                </div>
                            </RadioGroup>
                            {form.branch_scope === "specific" && (
                                <div className="mt-2 space-y-2">
                                    <div className="flex items-center justify-between gap-2">
                                        <div className="relative flex-1">
                                            <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                                            <Input value={branchQuery} onChange={(e) => setBranchQuery(e.target.value)}
                                                placeholder="Buscar sucursal…" className="pl-8 h-9"
                                                data-testid="portal-user-branch-search" />
                                        </div>
                                        <span className="text-xs text-muted-foreground whitespace-nowrap" data-testid="portal-user-branch-count">
                                            {form.branch_ids.length} seleccionada{form.branch_ids.length === 1 ? "" : "s"}
                                        </span>
                                    </div>
                                    {(() => {
                                        const filtered = branches.filter((b) =>
                                            (b.name || "").toLowerCase().includes(branchQuery.trim().toLowerCase()));
                                        return (
                                            <div className="max-h-52 overflow-auto border rounded-lg p-2 space-y-1">
                                                {filtered.map((b) => (
                                                    <label key={b.id} className="flex items-center gap-2 py-1 px-1 rounded hover:bg-secondary cursor-pointer">
                                                        <Checkbox checked={form.branch_ids.includes(b.id)}
                                                            onCheckedChange={() => toggleBranch(b.id)}
                                                            data-testid={`portal-user-branch-${b.id}`} />
                                                        <span className="text-sm">{b.name}</span>
                                                    </label>
                                                ))}
                                                {filtered.length === 0 && (
                                                    <p className="text-xs text-muted-foreground p-1">
                                                        {branches.length === 0 ? "No hay sucursales." : "Sin resultados para su búsqueda."}
                                                    </p>
                                                )}
                                            </div>
                                        );
                                    })()}
                                </div>
                            )}
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
                        <Button onClick={submit} disabled={saving} data-testid="portal-user-save">
                            {saving ? "Guardando…" : "Guardar"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Password reset dialog */}
            <Dialog open={!!pwUser} onOpenChange={(v) => { if (!v) { setPwUser(null); setNewPw(""); } }}>
                <DialogContent className="max-w-md" data-testid="portal-user-pw-dialog">
                    <DialogHeader>
                        <DialogTitle>Nueva contraseña</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-2 py-1">
                        <p className="text-sm text-muted-foreground">{pwUser?.full_name} — {pwUser?.email}</p>
                        <div className="flex gap-2">
                            <Input value={newPw} readOnly className="bg-muted/50 cursor-default font-mono" data-testid="portal-user-newpw" />
                            <Button type="button" variant="outline" size="icon" onClick={() => setNewPw(genPassword())} title="Generar">
                                <RefreshCw className="w-4 h-4" />
                            </Button>
                            <Button type="button" variant="outline" size="icon" onClick={() => { navigator.clipboard?.writeText(newPw); toast.success("Copiada"); }} title="Copiar">
                                <Copy className="w-4 h-4" />
                            </Button>
                        </div>
                        <p className="text-xs text-muted-foreground">Contraseña segura generada automáticamente. Regenérela si lo desea y cópiela para compartirla.</p>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => { setPwUser(null); setNewPw(""); }}>Cancelar</Button>
                        <Button onClick={submitPassword} data-testid="portal-user-pw-save">Actualizar</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
